import React, { useState, useEffect } from 'react';
import { Users, User as UserIcon } from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';
import { User } from '../../types';
import VerifiedBadge from '../VerifiedBadge';

interface WhosGoingListProps {
  eventId: string;
  totalAccepted: number;
  hostId?: string; // Optional host ID to include in the list
}

const WhosGoingList: React.FC<WhosGoingListProps> = ({ eventId, totalAccepted, hostId }) => {
  const [attendees, setAttendees] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);

  const DISPLAY_LIMIT = 8; // Show first 8 attendees

  useEffect(() => {
    const fetchAttendees = async () => {
      setIsLoading(true);
      try {
        // Fetch accepted attendees
        const { data, error } = await supabase
          .from('event_applications')
          .select(`
            applicant_id,
            profiles:applicant_id(
              id,
              username,
              full_name,
              avatar_url,
              is_verified
            )
          `)
          .eq('event_id', eventId)
          .eq('status', 'accepted')
          .order('created_at', { ascending: false })
          .limit(50); // Fetch up to 50 for display

        if (error) {
          console.error('Error fetching attendees:', error);
          return;
        }

        const attendeesList: User[] = [];

        // Add host first if hostId is provided
        if (hostId) {
          const { data: hostData, error: hostError } = await supabase
            .from('profiles')
            .select('id, username, full_name, avatar_url, is_verified')
            .eq('id', hostId)
            .maybeSingle();

          if (!hostError && hostData) {
            attendeesList.push({
              id: hostData.id,
              username: hostData.username || 'Host',
              fullName: hostData.full_name || '',
              avatarUrl: hostData.avatar_url,
              isVerified: hostData.is_verified || false,
              isHost: true, // Mark as host
            } as User & { isHost?: boolean });
          }
        }

        // Add accepted attendees (excluding host if they're already in the list)
        if (data) {
          const otherAttendees = data
            .filter((app: any) => app.profiles && app.profiles.id !== hostId)
            .map((app: any) => ({
              id: app.profiles.id,
              username: app.profiles.username || 'Unknown',
              fullName: app.profiles.full_name || '',
              avatarUrl: app.profiles.avatar_url,
              isVerified: app.profiles.is_verified || false,
            } as User));

          attendeesList.push(...otherAttendees);
        }

        setAttendees(attendeesList);
      } catch (err) {
        console.error('Error loading attendees:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAttendees();
  }, [eventId, hostId]);

  const displayedAttendees = showAll ? attendees : attendees.slice(0, DISPLAY_LIMIT);
  const remainingCount = totalAccepted - DISPLAY_LIMIT;

  if (isLoading) {
    return (
      <div className="bg-slate-800/50 rounded-2xl p-5 border-l-4 border-red-500">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Users size={18} className="text-gray-300" />
            <h3 className="font-semibold text-white text-[15px]">Who's Going</h3>
          </div>
        </div>
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex items-center gap-3 animate-pulse">
              <div className="w-9 h-9 bg-slate-700 rounded-full" />
              <div className="flex-1">
                <div className="h-3.5 bg-slate-700 rounded w-20 mb-1.5" />
                <div className="h-3 bg-slate-700 rounded w-24" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (attendees.length === 0) {
    return null;
  }

  return (
    <div className="bg-slate-800/50 rounded-2xl p-5 border-l-4 border-red-500">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Users size={18} className="text-gray-300" />
          <h3 className="font-semibold text-white text-[15px]">Who's Going</h3>
        </div>
        <span className="text-sm text-gray-400">
          {totalAccepted} {totalAccepted === 1 ? 'person' : 'people'}
        </span>
      </div>

      <div className="space-y-3">
        {displayedAttendees.map((attendee: any) => (
          <div
            key={attendee.id}
            className="flex items-center gap-3 rounded-lg p-2 -m-2"
          >
            {/* Avatar */}
            <div className="relative flex-shrink-0">
              {attendee.avatarUrl ? (
                <img
                  src={attendee.avatarUrl}
                  alt={attendee.username}
                  className="w-9 h-9 rounded-full object-cover"
                />
              ) : (
                <div className="w-9 h-9 rounded-full bg-slate-700 flex items-center justify-center">
                  <UserIcon size={16} className="text-gray-400" />
                </div>
              )}
            </div>

            {/* User Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <p className="text-sm font-medium text-white truncate">
                  {attendee.username}
                </p>
                {attendee.isHost && (
                  <span className="text-xs px-1.5 py-0.5 bg-red-500/20 text-red-400 rounded font-medium">
                    Host
                  </span>
                )}
                {attendee.isVerified && (
                  <VerifiedBadge size="sm" showTooltip={false} />
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Show more/less button */}
      {totalAccepted > DISPLAY_LIMIT && (
        <button
          onClick={() => setShowAll(!showAll)}
          className="w-full mt-4 py-2 text-sm font-medium text-gray-300 hover:text-white transition-colors"
        >
          {showAll
            ? 'Show less'
            : `Show ${remainingCount} more ${remainingCount === 1 ? 'person' : 'people'}`}
        </button>
      )}
    </div>
  );
};

export default WhosGoingList;
